import { useState, useEffect } from 'react';
import { messagingApi } from '../../services/messaging-api';
import { showToast } from '../../utils/toast';

interface ProviderFormProps {
  tenantId: string;
  providerId?: string;
  provider?: any;
  providers: any[];
  onSuccess: () => void;
  onCancel: () => void;
}

export function ProviderForm({
  tenantId,
  providerId,
  provider,
  providers,
  onSuccess,
  onCancel,
}: ProviderFormProps) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    tenantId,
    providerTypeId: provider?.providerTypeId || provider?.providerType?.id || '',
    name: provider?.name || '',
    senderId: provider?.senderId || '',
    apiKey: provider?.apiKey || '',
    apiSecret: provider?.apiSecret || '',
    apiUrl: provider?.apiUrl || '',
    isDefault: provider?.isDefault || false,
  });

  useEffect(() => {
    if (provider) {
      setFormData({
        tenantId,
        providerTypeId: provider.providerTypeId || provider.providerType?.id || '',
        name: provider.name || '',
        senderId: provider.senderId || '',
        apiKey: provider.apiKey || '',
        apiSecret: provider.apiSecret || '',
        apiUrl: provider.apiUrl || '',
        isDefault: provider.isDefault || false,
      });
    } else {
      setFormData({
        tenantId,
        providerTypeId: '',
        name: '',
        senderId: '',
        apiKey: '',
        apiSecret: '',
        apiUrl: '',
        isDefault: false,
      });
    }
  }, [provider, tenantId]);

  const handleChange = (e: any) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (providerId) {
        await messagingApi.updateProvider(providerId, formData);
        showToast.success('Provider updated successfully!');
      } else {
        await messagingApi.createProvider(formData);
        showToast.success('Provider created successfully!');
      }
      onSuccess();
    } catch (err: any) {
      showToast.error('Failed to save provider: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block font-semibold mb-2 text-gray-900 dark:text-white">Provider Type *</label>
        <select
          name="providerTypeId"
          value={formData.providerTypeId}
          onChange={handleChange}
          required
          className="border rounded-lg bg-white text-gray-900 px-3 py-2 w-full border-gray-300 focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-800 dark:text-white dark:border-gray-700"
        >
          <option value="">Select provider type</option>
          {providers.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="block font-semibold mb-2 text-gray-900 dark:text-white">Display Name *</label>
        <input
          type="text"
          name="name"
          value={formData.name}
          onChange={handleChange}
          required
          className="border rounded-lg bg-white text-gray-900 px-3 py-2 w-full border-gray-300 focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-800 dark:text-white dark:border-gray-700"
          placeholder="e.g., Main SMS Provider"
        />
      </div>

      <div>
        <label className="block font-semibold mb-2 text-gray-900 dark:text-white">Sender ID *</label>
        <input
          type="text"
          name="senderId"
          value={formData.senderId}
          onChange={handleChange}
          required
          className="border rounded-lg bg-white text-gray-900 px-3 py-2 w-full border-gray-300 focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-800 dark:text-white dark:border-gray-700"
          placeholder="e.g., QRYPTPOS or +1234567890"
        />
      </div>

      <div>
        <label className="block font-semibold mb-2 text-gray-900 dark:text-white">API Key *</label>
        <input
          type="password"
          name="apiKey"
          value={formData.apiKey}
          onChange={handleChange}
          required
          className="border rounded-lg bg-white text-gray-900 px-3 py-2 w-full border-gray-300 focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-800 dark:text-white dark:border-gray-700"
        />
      </div>

      <div>
        <label className="block font-semibold mb-2 text-gray-900 dark:text-white">API Secret (Optional)</label>
        <input
          type="password"
          name="apiSecret"
          value={formData.apiSecret}
          onChange={handleChange}
          className="border rounded-lg bg-white text-gray-900 px-3 py-2 w-full border-gray-300 focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-800 dark:text-white dark:border-gray-700"
        />
      </div>

      <div>
        <label className="block font-semibold mb-2 text-gray-900 dark:text-white">API URL (Optional)</label>
        <input
          type="url"
          name="apiUrl"
          value={formData.apiUrl}
          onChange={handleChange}
          className="border rounded-lg bg-white text-gray-900 px-3 py-2 w-full border-gray-300 focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-800 dark:text-white dark:border-gray-700"
        />
      </div>

      <label className="flex items-center">
        <input
          type="checkbox"
          name="isDefault"
          checked={formData.isDefault}
          onChange={handleChange}
          className="mr-2"
        />
        <span className="font-semibold">Set as default provider</span>
      </label>

      <div className="flex gap-3">
        <button
          type="submit"
          disabled={loading}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
        >
          {loading ? 'Saving...' : providerId ? 'Update Provider' : 'Create Provider'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="bg-gray-300 text-black px-4 py-2 rounded-lg hover:bg-gray-400"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
